/**
 * Global fetch interceptor for 401 (Unauthorized) responses.
 *
 * When the backend session expires, API calls return 401 but the frontend
 * continues showing the user as logged in. This interceptor detects 401
 * responses and forces a client-side logout with a toast notification.
 *
 * Excluded paths (these legitimately return 401):
 * - /user/login — wrong credentials
 * - /user/logout — already logged out
 * - /user?_format=json — userGetCurrent check (returns 401 when not logged in)
 * - /session/token — session token endpoint
 * - /user/register — registration flow
 * - /oauth — OAuth/SSO flows
 */

import toast from 'react-hot-toast';
import i18next from 'i18next';
import { ROUTE_ACCOUNT_LOGIN } from '../config/routes';

/**
 * URL path patterns that should NOT trigger the session expired redirect.
 * Matched against the pathname portion of the URL using exact prefix matching.
 */
const EXCLUDED_PATHS = [
  '/user/login',
  '/user/logout',
  '/user/register',
  '/session/token',
  '/oauth',
  '/entity/profile',
];

/**
 * Query-string patterns that indicate auth-check requests.
 * These return 401 legitimately when the user is not logged in.
 */
const EXCLUDED_URLS = [
  '/user?_format=json',
];

/** Prevent multiple concurrent redirects from overlapping 401 responses. */
let isRedirecting = false;

/**
 * Checks if a URL matches any excluded path using pathname-based matching.
 * Uses URL parsing for path segments to avoid substring false positives
 * (e.g., '/user/login-history' should NOT match '/user/login').
 */
const isExcludedUrl = (url: string): boolean => {
  // Check exact URL patterns first (includes query string matching).
  if (EXCLUDED_URLS.some((pattern) => url.includes(pattern))) {
    return true;
  }

  // Parse pathname for prefix matching against path segments.
  try {
    const { pathname } = new URL(url);
    return EXCLUDED_PATHS.some((path) => {
      if (!pathname.startsWith(path)) {
        return false;
      }
      // Ensure we match complete path segments: /user/login must not match
      // /user/login-history. After the prefix, the next char must be
      // end-of-string, '/', or '?'.
      const nextChar = pathname[path.length];
      return nextChar === undefined || nextChar === '/' || nextChar === '?';
    });
  } catch {
    // Relative URLs — fall back to simple includes check.
    return EXCLUDED_PATHS.some((path) => url.includes(path));
  }
};

/**
 * Handles a 401 response by showing a toast and redirecting to login.
 */
const handleSessionExpired = (): void => {
  if (isRedirecting) {
    return;
  }
  isRedirecting = true;

  const message = i18next.t(
    'session.expired',
    'Je sessie is verlopen. Log opnieuw in.',
  );
  toast.error(message, { duration: 4000 });

  // Build login URL with destination to return the user to their current page.
  const destination = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  const loginUrl = `${ROUTE_ACCOUNT_LOGIN}?destination=${destination}&expired=1`;

  // Short delay so the toast is visible before redirect.
  // Do NOT reset isRedirecting — the page will be replaced by navigation.
  setTimeout(() => {
    window.location.href = loginUrl;
  }, 1500);
};

/**
 * Installs the global fetch interceptor.
 * Call once during app initialization (e.g., in src/index.jsx).
 */
const installSessionExpiredInterceptor = (): void => {
  const originalFetch = window.fetch;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const response = await originalFetch(input, init);

    if (response.status === 401) {
      let url: string;
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof Request) {
        url = input.url;
      } else {
        url = input.toString();
      }

      if (!isExcludedUrl(url)) {
        handleSessionExpired();
      }
    }

    return response;
  };
};

export default installSessionExpiredInterceptor;
